/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { MeditationResponse, meditationService } from '../services/meditationService';

// Keys for localStorage
const STORAGE_KEYS = {
  MEDITATION: 'luminous_last_meditation',
  POSITION: 'luminous_last_position',
  TIMESTAMP: 'luminous_last_timestamp'
};

interface PlaybackContextType {
  currentMeditation: MeditationResponse | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  volume: number;
  isMiniPlayerVisible: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  
  // Control methods
  playMeditation: (meditation: MeditationResponse) => void;
  pauseMeditation: () => void;
  resumeMeditation: () => void;
  stopMeditation: () => void;
  setCurrentTime: (time: number) => void;
  setVolume: (volume: number) => void;
  togglePlayPause: () => void;
  hideMiniPlayer: () => void;
  showMiniPlayer: () => void;
}

export const PlaybackContext = createContext<PlaybackContextType | undefined>(undefined);

export const PlaybackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentMeditation, setCurrentMeditation] = useState<MeditationResponse | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(80); // Default volume at 80%
  const [isMiniPlayerVisible, setIsMiniPlayerVisible] = useState(false);
  
  // Flag to track if we've already attempted to restore the state on initial load
  const hasAttemptedRestore = useRef(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const hasTrackedPlay = useRef(false);
  const saveStateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasPlayingBeforeHide = useRef(false);

  // Save current playback state to localStorage
  const savePlaybackState = () => {
    if (!currentMeditation) return;
    
    try {
      // Save meditation data
      localStorage.setItem(STORAGE_KEYS.MEDITATION, JSON.stringify(currentMeditation));
      
      // Save current position
      localStorage.setItem(STORAGE_KEYS.POSITION, currentTime.toString());
      
      // Save timestamp of when this was stored
      localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
    } catch (error) {
      console.error('Error saving playback state to localStorage:', error);
    }
  };
  
  // Load saved playback state from localStorage
  const loadPlaybackState = () => {
    try {
      // Check if we have saved data
      const savedMeditationJson = localStorage.getItem(STORAGE_KEYS.MEDITATION);
      const savedPosition = localStorage.getItem(STORAGE_KEYS.POSITION);
      const savedTimestamp = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);
      
      if (!savedMeditationJson || !savedPosition || !savedTimestamp) return false;
      
      // Parse the saved data
      const savedMeditation = JSON.parse(savedMeditationJson) as MeditationResponse;
      const position = parseFloat(savedPosition);
      const timestamp = parseInt(savedTimestamp, 10);
      
      // Check if the saved state is too old (older than 24 hours)
      const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const now = Date.now();
      if (now - timestamp > MAX_AGE) {
        // Clear outdated storage
        localStorage.removeItem(STORAGE_KEYS.MEDITATION);
        localStorage.removeItem(STORAGE_KEYS.POSITION);
        localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
        return false;
      }
      
      // Update state with saved values
      setCurrentMeditation(savedMeditation);
      setCurrentTime(position);
      setProgress((position / (savedMeditation.duration || 1)) * 100);
      setDuration(savedMeditation.duration || 0);
      setIsMiniPlayerVisible(true);
      
      // Set the audio source but don't play it yet
      if (audioRef.current) {
        audioRef.current.src = savedMeditation.audioUrl || '';
        audioRef.current.currentTime = position;
        audioRef.current.load();
      }
      
      return true;
    } catch (error) {
      console.error('Error loading playback state from localStorage:', error);
      return false;
    }
  };
  
  // Initialize audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleTimeUpdate = () => {
      if (!audio) return;
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / (audio.duration || 1)) * 100);

      // Keep Android's media framework updated with current position so it
      // maintains audio focus and continues playback when the screen is off
      if ('mediaSession' in navigator && audio.duration) {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          playbackRate: audio.playbackRate,
          position: audio.currentTime,
        });
      }
    };
    
    const handleDurationChange = () => {
      if (!audio) return;
      setDuration(audio.duration);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Try to restore playback state when component mounts
  useEffect(() => {
    if (!hasAttemptedRestore.current) {
      hasAttemptedRestore.current = true;
      loadPlaybackState();
    }
  }, []);
  
  // Save playback state when changes occur
  useEffect(() => {
    // Only save state if there's an active meditation
    if (currentMeditation && currentTime > 0) {
      // Clear any existing timeout
      if (saveStateTimeoutRef.current) {
        clearTimeout(saveStateTimeoutRef.current);
      }
      
      // Set a new timeout to debounce frequent updates
      saveStateTimeoutRef.current = setTimeout(() => {
        savePlaybackState();
      }, 1000); // Save after 1 second of no updates
    }
    
    return () => {
      if (saveStateTimeoutRef.current) {
        clearTimeout(saveStateTimeoutRef.current);
      }
    };
  }, [currentMeditation, currentTime]);
  
  // Save state before unloading the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentMeditation) {
        savePlaybackState();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentMeditation, currentTime]);
  
  // Listen for page visibility changes to handle background/foreground playback
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Record whether we were playing before the screen turned off
        wasPlayingBeforeHide.current = isPlaying;
        // Save state in case the OS kills the app
        savePlaybackState();
      } else if (document.visibilityState === 'visible') {
        // Screen was unlocked — if Android suspended the audio while we were
        // "playing", try to resume automatically
        const audio = audioRef.current;
        if (wasPlayingBeforeHide.current && audio && audio.paused && currentMeditation) {
          audio.play()
            .then(() => {
              setIsPlaying(true);
              if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
              }
            })
            .catch(error => {
              console.error('Error resuming audio after screen unlock:', error);
            });
        }
      }
    };

    // Define the beforeunload handler in this scope
    const handleBeforeUnload = () => {
      if (currentMeditation) {
        savePlaybackState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentMeditation, currentTime, isPlaying]);
  
  // Listen for user logout event to stop playback and hide mini player
  useEffect(() => {
    const handleUserLogout = () => {
      stopMeditation();
      hideMiniPlayer();
    };
    
    window.addEventListener('user-logout', handleUserLogout);
    
    return () => {
      window.removeEventListener('user-logout', handleUserLogout);
    };
  }, []);
  
  // Handle volume changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume / 100;
    }
  }, [volume]);

  // Setup media session controls
  useEffect(() => {
    if ('mediaSession' in navigator && currentMeditation) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentMeditation.title,
        artist: 'Rupert Spira',
        album: 'Luminous',
        artwork: [
          { src: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png' },
          { src: '/logo192.png', sizes: '192x192', type: 'image/png' },
          { src: '/logo512.png', sizes: '512x512', type: 'image/png' },
        ]
      });

      navigator.mediaSession.setActionHandler('play', resumeMeditation);
      navigator.mediaSession.setActionHandler('pause', pauseMeditation);
      
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (audioRef.current && details.seekTime !== undefined) {
          audioRef.current.currentTime = details.seekTime;
        }
      });
      
      return () => {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      };
    }
  }, [currentMeditation]);

  const playMeditation = (meditation: MeditationResponse) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    // If a different meditation is already playing, reset the audio
    if (currentMeditation?.hash !== meditation.hash) {
      audio.pause();
      setCurrentTime(0);
      setProgress(0);
      hasTrackedPlay.current = false;
      
      // Set the new meditation data
      setCurrentMeditation(meditation);
      
      // Set the audio source
      audio.src = meditation.audioUrl || '';
      audio.load();
    }
    
    // Play the audio
    audio.play()
      .then(() => {
        setIsPlaying(true);
        setIsMiniPlayerVisible(true);

        // Tell Android's media framework we are actively playing so it keeps
        // audio alive when the screen turns off
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
        }

        // Track that user started listening to this meditation
        if (meditation.hash) {
          meditationService.trackStarted(meditation.hash)
            .catch(error => {
              console.error('Error tracking meditation start:', error);
              // Non-critical error, don't interrupt playback
            });
        }
      })
      .catch(error => {
        console.error('Error playing audio:', error);
      });
  };

  const pauseMeditation = () => {
    const audio = audioRef.current;
    if (audio && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      
      // Update navigator.mediaSession playback state
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    }
  };

  const resumeMeditation = () => {
    const audio = audioRef.current;
    if (audio && !isPlaying && currentMeditation) {
      audio.play()
        .then(() => {
          setIsPlaying(true);
          
          // Update navigator.mediaSession playback state
          if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'playing';
          }
        })
        .catch(error => {
          console.error('Error resuming playback:', error);
        });
    }
  };

  const stopMeditation = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      
      // Update navigator.mediaSession playback state
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
      }
    }
  };

  const setCurrentTimeManually = (time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setCurrentTime(time);
      setProgress((time / (audio.duration || 1)) * 100);
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseMeditation();
    } else {
      resumeMeditation();
    }
  };

  const hideMiniPlayer = () => {
    setIsMiniPlayerVisible(false);
  };

  const showMiniPlayer = () => {
    if (currentMeditation) {
      setIsMiniPlayerVisible(true);
    }
  };

  const value = {
    currentMeditation,
    isPlaying,
    currentTime,
    duration,
    progress,
    volume,
    isMiniPlayerVisible,
    audioRef,
    
    playMeditation,
    pauseMeditation,
    resumeMeditation,
    stopMeditation,
    setCurrentTime: setCurrentTimeManually,
    setVolume,
    togglePlayPause,
    hideMiniPlayer,
    showMiniPlayer,
  };

  return (
    <PlaybackContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" />
    </PlaybackContext.Provider>
  );
};

export const usePlayback = () => {
  const context = useContext(PlaybackContext);
  if (context === undefined) {
    throw new Error('usePlayback must be used within a PlaybackProvider');
  }
  return context;
};
